import { getIdentifier, MedplumClient } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { Systems } from "@/fhir/enums/systems";
import { AllowedFileTypes } from "@/types/uploads";
import { usDateFormat } from "@/utils/datetime";
import { getCodeableConceptCode, getCodeableConceptCoding } from "@/utils/medplum";
import { showSystemErrorNotification, toast } from "@/utils/notifications";
import { AttachmentVerification, ensureFileExtension, getFileBlob } from "@/utils/uploads";

import { UploadedFileData, VISIBLE_TO_CLIENT } from "../types/file-uploads";
import { FileUploadFormType } from "../zod-schemas/file-uploads";

export function getInitialValuesFromDocumentReference(
  documentReference: DocumentReference,
): FileUploadFormType {
  const attachment = documentReference.content?.[0]?.attachment;
  const file = new File(["..."], attachment?.title ?? "", {
    type: attachment?.contentType ?? "",
  });

  return {
    fileName: getIdentifier(documentReference, Systems.QH) ?? "",
    file,
    fileType: getCodeableConceptCoding(documentReference.type) ?? null,
    description: documentReference.description ?? "",
    visibleToClient:
      getCodeableConceptCode(documentReference.securityLabel?.[0]) === VISIBLE_TO_CLIENT.code,
    fileChanged: false,
  };
}

export function parseDocumentReferenceFields(
  documentReference: DocumentReference,
): UploadedFileData {
  const id = documentReference.id;
  const name = (documentReference.identifier?.[0]?.value || "").trim();
  const type = (documentReference.type?.coding?.[0]?.display || "").trim();
  const description = (documentReference.description || "").trim();
  const dateAdded = documentReference.date ? usDateFormat(documentReference.date) : "";
  const url = documentReference.content?.[0]?.attachment?.url;
  const contentType = documentReference.content?.[0]?.attachment?.contentType as AllowedFileTypes;
  const addedBy = documentReference.author?.[0]?.display ?? "";
  const isVisibleToClient =
    documentReference.securityLabel?.[0]?.coding?.[0]?.code === VISIBLE_TO_CLIENT.code;
  const lastUpdated = documentReference.meta?.lastUpdated;
  return {
    id,
    name,
    type,
    description,
    dateAdded,
    addedBy,
    isVisibleToClient,
    url,
    contentType,
    lastUpdated,
  };
}

export async function viewFile(
  medplum: MedplumClient,
  name: string,
  url: string | undefined,
  verification?: AttachmentVerification,
) {
  // Opened synchronously, before the await below, so the browser still credits this call with the
  // click's transient activation and does not block it as an unrequested popup.
  const previewTab = window.open("", "_blank");

  if (!previewTab) {
    toast({
      type: "warning",
      title: "Preview blocked",
      message: "Your browser blocked the preview tab. Allow pop-ups for this site and try again.",
      autoClose: false,
    });
    return;
  }

  try {
    const blob = await getFileBlob(medplum, name, url, verification);
    const objectUrl = URL.createObjectURL(blob);
    previewTab.location.href = objectUrl;
    // Registered after navigating: the tab's initial about:blank load also fires `onload`, and a
    // handler attached earlier would revoke the URL before the attachment has loaded.
    previewTab.onload = () => URL.revokeObjectURL(objectUrl);
  } catch (error) {
    previewTab.close();
    throw error;
  }
}

export async function downloadFile(
  medplum: MedplumClient,
  name: string,
  url: string | undefined,
  contentType?: string,
  verification?: AttachmentVerification,
) {
  const link = document.createElement("a");
  let objectUrl: string | undefined;
  try {
    // Fetch the file as a blob
    const blob = await getFileBlob(medplum, name, url, verification);

    // Force the blob to be treated as an octet-stream to prevent browser from opening it
    const downloadBlob = new Blob([blob], { type: "application/octet-stream" });

    objectUrl = URL.createObjectURL(downloadBlob);

    const fileName = ensureFileExtension(name, contentType);

    // Create temporary link and trigger download
    link.href = objectUrl;
    link.download = fileName;
    link.target = "_self"; // Ensure it doesn't open in new tab
    link.rel = "noopener noreferrer";

    // Make the link invisible and add to DOM
    link.style.display = "none";
    document.body.appendChild(link);

    // Trigger download
    link.click();
  } catch (error) {
    showSystemErrorNotification();
    throw error;
  } finally {
    if (document.body.contains(link)) {
      document.body.removeChild(link);
    }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
